/**
 * 科目历史API函数
 * 提供获取和操作科目考试历史记录的相关功能
 */
import { supabase } from '../supabaseClient';

/**
 * 生成唯一ID
 * @returns {string} 唯一ID
 */
export const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : ((r & 0x3) | 0x8);
    return v.toString(16);
  });
};

/**
 * 获取用户科目考试历史记录
 * @param {string} userId - 用户ID
 * @param {object} options - 查询选项
 * @returns {Promise<Array>} 历史记录列表
 */
export const getUserSubjectHistory = async (userId, options = {}) => {
  try {
    if (!userId) throw new Error('用户ID不能为空');
    
    const { limit, examId, subjectId, orderBy = 'created_at', ascending = false } = options;
    
    let query = supabase
      .from('subject_historys')
      .select('*')
      .eq('user_id', userId);
      
    if (examId) {
      query = query.eq('exam_id', Number(examId));
    }
    
    if (subjectId) {
      query = query.eq('subject_id', Number(subjectId));
    }
    
    query = query.order(orderBy, { ascending });
    
    if (limit) {
      query = query.limit(limit);
    }
    
    const { data, error } = await query;
    
    if (error) throw error;
    
    return data || [];
  } catch (error) {
    console.error('获取科目历史记录出错:', error);
    throw error;
  }
};

/**
 * 获取最近的一条科目历史记录
 * @param {string} userId - 用户ID
 * @param {number} examId - 考试ID
 * @param {number} subjectId - 科目ID
 * @returns {Promise<Object|null>} 历史记录
 */
export const getLatestSubjectHistory = async (userId, examId, subjectId) => {
  try {
    if (!userId) throw new Error('用户ID不能为空');
    
    const { data, error } = await supabase
      .from('subject_historys')
      .select('*')
      .eq('user_id', userId)
      .eq('exam_id', Number(examId))
      .eq('subject_id', Number(subjectId))
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
      
    if (error) throw error;
    
    return data;
  } catch (error) {
    console.error('获取最近科目历史记录出错:', error);
    throw error;
  }
};

/**
 * 添加科目考试历史记录
 * @param {object} historyData - 历史记录数据
 * @returns {Promise<Object>} 创建的历史记录
 */
export const addSubjectHistory = async (historyData) => {
  try {
    if (!historyData.user_id) throw new Error('用户ID不能为空');
    if (!historyData.exam_id) throw new Error('考试ID不能为空');
    if (!historyData.subject_id) throw new Error('科目ID不能为空');
    
    // 生成唯一ID，如果没有提供
    if (!historyData.id) {
      historyData.id = generateUUID();
    }
    
    // 确保创建时间存在
    if (!historyData.created_at) {
      historyData.created_at = new Date().toISOString();
    }
    
    const { data, error } = await supabase
      .from('subject_historys')
      .insert([historyData])
      .select();
      
    if (error) throw error;
    
    return data[0];
  } catch (error) {
    console.error('添加科目历史记录出错:', error);
    throw error;
  }
};

/**
 * 获取学习统计数据
 * @param {string} userId - 用户ID
 * @returns {Promise<Object>} 学习统计数据
 */
export const getLearningStats = async (userId) => {
  try {
    if (!userId) throw new Error('用户ID不能为空');
    
    // 检查用户是否是旧版体验用户
    if (userId === 'experience-user') {
      // 为旧版体验用户返回模拟数据
      return {
        learningDays: 1,
        totalQuestions: 20,
        averageScore: 80,
        overallAccuracy: 75,
        recordsCount: 2,
        lastStudyDate: new Date()
      };
    }
    
    // 检查是否是新版体验用户（使用随机分配的ID）
    const isExperienceUser = userId.startsWith('exp-');
    
    // 如果是新体验用户但没有学习记录，生成一些初始数据
    if (isExperienceUser) {
      const { data, error } = await supabase
        .from('subject_historys')
        .select('*')
        .eq('user_id', userId);
      
      if (error) throw error;
      
      // 如果该体验用户没有学习记录，返回一些初始数据
      if (!data || data.length === 0) {
        return {
          learningDays: 0,
          totalQuestions: 0,
          averageScore: 0,
          overallAccuracy: 0,
          recordsCount: 0,
          lastStudyDate: null
        };
      }
    }
    
    // 直接从subject_historys表中查询用户的历史记录
    const { data, error } = await supabase
      .from('subject_historys')
      .select('*')
      .eq('user_id', userId);
    
    if (error) throw error;
    
    const subjectHistory = data || [];
    
    // 计算学习天数
    const uniqueDates = new Set();
    subjectHistory.forEach(history => {
      if (history.created_at) {
        const date = new Date(history.created_at).toDateString();
        uniqueDates.add(date);
      }
    });
    const learningDays = uniqueDates.size;
    
    // 计算累计答题数
    const totalQuestions = subjectHistory.reduce((sum, h) => {
      const questions = parseInt(h.total_questions) || 0;
      return sum + questions;
    }, 0);
    
    // 计算平均分
    let averageScore = 0;
    if (subjectHistory.length > 0) {
      const totalScore = subjectHistory.reduce((sum, h) => {
        return sum + (parseInt(h.score) || 0);
      }, 0);
      averageScore = Math.round(totalScore / subjectHistory.length);
    }
    
    // 计算总体正确率
    let totalQuestionsCount = 0;
    let totalCorrect = 0;
    subjectHistory.forEach(h => {
      totalQuestionsCount += (parseInt(h.total_questions) || 0);
      totalCorrect += (parseInt(h.correct_count) || 0);
    });
    const overallAccuracy = totalQuestionsCount > 0 
      ? Math.round((totalCorrect / totalQuestionsCount) * 100) 
      : 0;
    
    return {
      learningDays,
      totalQuestions,
      averageScore,
      overallAccuracy,
      recordsCount: subjectHistory.length,
      lastStudyDate: subjectHistory.length > 0 
        ? new Date(Math.max(...subjectHistory.map(h => new Date(h.created_at || 0)))) 
        : null
    };
  } catch (error) {
    console.error('获取学习统计数据出错:', error);
    // 出错时返回默认值，避免前端出错
    return {
      learningDays: 0,
      totalQuestions: 0,
      averageScore: 0,
      overallAccuracy: 0,
      recordsCount: 0,
      lastStudyDate: null
    };
  }
};

/**
 * 获取科目统计数据
 * @param {string} userId - 用户ID
 * @param {number} subjectId - 科目ID
 * @returns {Promise<Object>} 科目统计数据
 */
export const getSubjectStats = async (userId, subjectId) => {
  try {
    if (!userId) throw new Error('用户ID不能为空');
    if (!subjectId) throw new Error('科目ID不能为空');
    
    const { data, error } = await supabase
      .from('subject_historys')
      .select('*')
      .eq('user_id', userId)
      .eq('subject_id', Number(subjectId));
      
    if (error) throw error;
    
    const subjectHistory = data || [];
    
    if (subjectHistory.length === 0) {
      return {
        answeredCount: 0,
        correctCount: 0,
        correctRate: 0,
        averageScore: 0,
        highestScore: 0,
        lowestScore: 0,
        trend: 0,
        lastStudied: null
      };
    }
    
    // 计算正确率和总题数
    let totalQuestions = 0;
    let totalCorrect = 0;
    
    subjectHistory.forEach(h => {
      const questions = parseInt(h.total_questions) || 0;
      const correct = parseInt(h.correct_count) || 0;
      totalQuestions += questions;
      totalCorrect += correct;
    });
    
    const correctRate = totalQuestions > 0 
      ? Math.round((totalCorrect / totalQuestions) * 100) 
      : 0;
    
    // 计算平均分
    const totalScore = subjectHistory.reduce((sum, h) => {
      return sum + (parseInt(h.score) || 0);
    }, 0);
    const averageScore = Math.round(totalScore / subjectHistory.length);
    
    // 最高分和最低分
    const scores = subjectHistory.map(h => parseInt(h.score) || 0);
    const highestScore = Math.max(...scores);
    const lowestScore = Math.min(...scores);
    
    // 计算进步趋势
    let trend = 0;
    if (subjectHistory.length >= 2) {
      const sorted = [...subjectHistory].sort((a, b) => 
        new Date(a.created_at || 0) - new Date(b.created_at || 0)
      );
      const firstScore = parseInt(sorted[0].score) || 0;
      const lastScore = parseInt(sorted[sorted.length - 1].score) || 0;
      trend = lastScore - firstScore;
    }
    
    // 获取最后学习时间
    const lastStudied = subjectHistory.reduce((latest, h) => {
      if (!h.created_at) return latest;
      const current = new Date(h.created_at);
      return !latest || current > latest ? current : latest;
    }, null);
    
    return {
      answeredCount: totalQuestions,
      correctCount: totalCorrect,
      correctRate,
      averageScore,
      highestScore,
      lowestScore,
      trend,
      lastStudied,
      recordsCount: subjectHistory.length
    };
  } catch (error) {
    console.error('获取科目统计数据出错:', error);
    throw error;
  }
};

/**
 * 删除科目历史记录
 * @param {string} recordId - 记录ID
 * @returns {Promise<void>}
 */
export const deleteSubjectHistory = async (recordId) => {
  try {
    if (!recordId) throw new Error('记录ID不能为空');
    
    const { error } = await supabase
      .from('subject_historys')
      .delete()
      .eq('id', recordId);
      
    if (error) throw error;
  } catch (error) {
    console.error('删除科目历史记录出错:', error);
    throw error;
  }
};

/**
 * 计算科目学习进度
 * @param {string} userId - 用户ID
 * @param {number} examId - 考试ID
 * @returns {Promise<Object>} 学习进度数据
 */
export const calculateExamProgress = async (userId, examId) => {
  try {
    if (!userId) throw new Error('用户ID不能为空');
    if (!examId) throw new Error('考试ID不能为空');
    
    // 获取考试下的所有科目
    const { data: subjects, error: subjectsError } = await supabase
      .from('subjects')
      .select('*')
      .eq('pid', Number(examId));
      
    if (subjectsError) throw subjectsError;
    
    // 获取用户在该考试下的所有科目历史记录
    const { data: history, error: historyError } = await supabase
      .from('subject_historys')
      .select('*')
      .eq('user_id', userId)
      .eq('exam_id', Number(examId));
      
    if (historyError) throw historyError;
    
    const subjectIds = subjects ? subjects.map(s => s._id) : [];
    const completedSubjectIds = new Set(history.map(h => h.subject_id));
    
    // 已完成的科目数量
    const completedCount = subjectIds.filter(id => completedSubjectIds.has(id)).length;
    
    // 计算平均分
    let totalScore = 0;
    const subjectScores = {};
    
    history.forEach(h => {
      // 对于每个科目，只取最高分
      const subjectId = h.subject_id;
      const score = parseInt(h.score) || 0;
      
      if (!subjectScores[subjectId] || score > subjectScores[subjectId]) {
        subjectScores[subjectId] = score;
      }
    });
    
    // 计算所有科目的平均分
    Object.values(subjectScores).forEach(score => {
      totalScore += score;
    });
    
    const averageScore = Object.keys(subjectScores).length > 0
      ? Math.round(totalScore / Object.keys(subjectScores).length)
      : 0;
    
    return {
      totalSubjects: subjectIds.length,
      completedSubjects: completedCount,
      completionRate: subjectIds.length > 0 
        ? Math.round((completedCount / subjectIds.length) * 100) 
        : 0,
      averageScore,
      subjectScores
    };
  } catch (error) {
    console.error('计算考试进度出错:', error);
    throw error;
  }
};

/**
 * 获取指定考试下的科目数量
 * @param {number} examId - 考试ID
 * @returns {Promise<number>} 科目数量
 */
export const getExamSubjectsCount = async (examId) => {
  try {
    if (!examId) throw new Error('考试ID不能为空');
    
    const { count, error } = await supabase
      .from('subjects')
      .select('*', { count: 'exact', head: true })
      .eq('pid', Number(examId));
      
    if (error) throw error;
    
    return count || 0;
  } catch (error) {
    console.error('获取考试科目数量出错:', error);
    return 0;
  }
}; 